/*
PURPOSE:
Puts the page's typeface into a generated PDF. The app renders in Inter, but jsPDF only ships
the PDF core fonts, so Inter has to be embedded before anything is drawn.

The .ttf files in `app/javascript/fonts` are static instances of Google Fonts' Inter variable
font (opsz=14, wght=400/500/700), subset to Latin-1 plus the typographic punctuation the
receipts use — about 17KB each. They stay separate assets and are fetched on demand, so only a
download pays for them.

Callers pass the family they get back to `setFont`, and fall back gracefully: if a font can't
be fetched, the PDF is still produced in Helvetica rather than not at all.
*/

import regular from "~/fonts/Inter-Regular.ttf?url";
import medium from "~/fonts/Inter-Medium.ttf?url";
import bold from "~/fonts/Inter-Bold.ttf?url";

export const FALLBACK_FONT = "helvetica";

// Helvetica has no medium, so emphasis at the page's weight 500 lands on bold instead.
const FALLBACK_STYLES = { medium: "bold" };

// `medium` carries the page's weight 500, which is what these documents emphasise with; 700 is
// reserved for the section divider labels, as in the stylesheet.
const INTER_WEIGHTS = [
  { file: "Inter-Regular.ttf", style: "normal", url: regular },
  { file: "Inter-Medium.ttf", style: "medium", url: medium },
  { file: "Inter-Bold.ttf", style: "bold", url: bold },
];

export const fontStyle = (family, style) =>
  family === FALLBACK_FONT ? (FALLBACK_STYLES[style] ?? style) : style;

// TrueType's version tag. jsPDF's virtual file system takes either base64 or a binary string,
// and it tells them apart by looking for this at the front, so the bytes go over unencoded.
const TTF_MAGIC = "\x00\x01\x00\x00";

async function fetchAsBinaryString(url) {
  const response = await fetch(url);

  if (!response.ok) throw new Error(`Could not load ${url}: ${response.status}`);

  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  // A proxy or dev server answering 200 with something that isn't the font would otherwise
  // reach jsPDF and fail there, out of reach of this catch.
  if (!binary.startsWith(TTF_MAGIC)) throw new Error(`${url} is not a TrueType font`);

  return binary;
}

export async function registerInter(doc) {
  try {
    const fonts = await Promise.all(
      INTER_WEIGHTS.map(async (weight) => ({
        ...weight,
        data: await fetchAsBinaryString(weight.url),
      }))
    );

    fonts.forEach(({ file, style, data }) => {
      doc.addFileToVFS(file, data);
      doc.addFont(file, "Inter", style);
    });

    return "Inter";
  } catch {
    return FALLBACK_FONT;
  }
}
